/**
 * The menu-view switcher on a public place page.
 *
 * WHAT THIS IS. The web twin of the Chow app's design/list toggle
 * (`menuraq-chow-mobile` → `app/place/[id]/menu.tsx`): one control that answers
 * "show me this menu as the platform draws it, or as the restaurant drew it".
 * The web page had HALF of it — `MenuDisplayPage` read a `design | list` value
 * out of localStorage and never wrote one — and no control at all, so the choice
 * a guest could make in the app had no equivalent on the web.
 *
 * WHY THE URL AND NOT localStorage. The Chow app keeps its choice on the
 * device (AsyncStorage `chow:menu-view:v1`), which is right for an app: there
 * is no address to put it in. The web has one, and an address is strictly
 * better — it survives a reload, it can be pasted to a colleague, and the
 * server can resolve the template BEFORE the first paint instead of after it.
 * That last point is the whole reason this is a query parameter and not client
 * state: the menu body is chosen during server render, and a body that is
 * chosen after hydration is a body that flashes.
 *
 * ONE PARAMETER, TWO BODIES.
 *   `?view=default` — Menuraq's own menu body (`PlaceMenuList`).
 *   `?view=design`  — the branch's own template, through the template engine
 *                     (`TemplateRenderer`), the same contract the Chow app
 *                     uses.
 *
 * TWO BODIES, NOT A GALLERY. A guest is offered a choice of TWO — the
 * platform's menu, or the design the restaurant published for THIS branch. Not
 * a catalogue of other designs: the template gallery belongs to the merchant,
 * who picks a design in the dashboard and publishes it per branch. What a
 * guest gets here is the switch the Chow app has, and nothing else.
 *
 * NOT A CUSTOMER CONTROL. The two bodies are not two looks at the same menu —
 * one is Menuraq's platform design and the other is the design the merchant
 * published. The merchant's choice is the per-branch setting, and this
 * switcher never changes it: it changes what THIS guest is looking at, for as
 * long as they look at it. See `components/place/PlaceMenuViewSwitcher.tsx`.
 *
 * @location lib/customer/place-menu-view.ts
 */

export type PlaceMenuViewMode = 'default' | 'design';

/** Query key carrying the body choice. */
export const PLACE_MENU_VIEW_PARAM = 'view';

/** Branch-scoped cookie for remembering the guest's last menu body. */
export const PLACE_MENU_VIEW_COOKIE_PREFIX = 'mnrq_menu_view_';

export function placeMenuViewPreferenceCookieName(branchId: string): string {
  return `${PLACE_MENU_VIEW_COOKIE_PREFIX}${branchId}`;
}

/**
 * The body choice a query string asks for, or null when it asks for nothing.
 *
 * Null is NOT "default". Null means "no opinion" — the caller decides, and the
 * caller is the page, which falls back to the merchant's own template when the
 * branch has one. Collapsing null into `default` would silently override the
 * merchant's design for every guest who arrived without a parameter, which is
 * every guest.
 */
export function parsePlaceMenuViewMode(raw: string | null | undefined): PlaceMenuViewMode | null {
  if (raw === 'default' || raw === 'design') return raw;
  return null;
}

/**
 * Whether a template has anything to PAINT.
 *
 * "Has a valid template" is not enough: that test is satisfied by an `engine`
 * alone, and a template that declares an engine but carries no nodes and no
 * pages renders an EMPTY body. The Chow app gates on the same thing
 * (`menuraq-chow-mobile` → `app/place/[id]/menu.tsx`, `hasDesign`) and for the
 * same reason: a guest who pressed a button and got a blank page would rather
 * have the menu.
 *
 * A paged-flip template carries its content in `nodes` too (its `pagedFlip`
 * config describes how those nodes are paginated), so `nodes` is the check that
 * covers every engine.
 */
export function hasRenderableTemplateBody(template: unknown): boolean {
  if (!template || typeof template !== 'object') return false;
  const t = template as { nodes?: unknown; pagedNodes?: unknown; pages?: unknown; engine?: unknown; pagedFlip?: unknown };
  if (Array.isArray(t.nodes) && t.nodes.length > 0) return true;
  if (Array.isArray(t.pagedNodes) && t.pagedNodes.length > 0) return true;
  if (t.pagedNodes && typeof t.pagedNodes === 'object' && Object.keys(t.pagedNodes).length > 0) return true;
  if (Array.isArray(t.pages) && t.pages.length > 0) return true;
  return false;
}

export interface PlaceMenuViewResolution {
  /** The body a guest with no parameter gets. */
  defaultView: PlaceMenuViewMode;
  /** The body on screen right now. */
  activeView: PlaceMenuViewMode;
  /** True when the template's own body paints the screen. */
  showTemplateBody: boolean;
}

/**
 * The body on screen, given the guest's request and what the branch published.
 *
 * With no request at all, the default is the merchant's own template when the
 * branch has one — the whole point of a branch publishing a template — and
 * Menuraq's body when it does not. That default is deliberately NOT changed by
 * this feature: a branch whose template renders today renders exactly as it did
 * before the switcher existed.
 *
 * A request for the design view is honoured only when there is a design to
 * show. The platform body is the floor: a template assigned to a branch can
 * never cost the guest the menu.
 */
export function resolvePlaceMenuView(input: {
  /** The guest's request, or null when they made none. */
  requested: PlaceMenuViewMode | null;
  /** Whether the template has content to paint. */
  hasRenderableBody: boolean;
  /** The resolved template engine. */
  engine: string;
}): PlaceMenuViewResolution {
  const canShowDesign = input.hasRenderableBody && input.engine !== 'storefront';
  const defaultView: PlaceMenuViewMode = canShowDesign ? 'design' : 'default';
  const activeView = input.requested ?? defaultView;
  return {
    defaultView,
    activeView,
    showTemplateBody: activeView === 'design' && input.hasRenderableBody,
  };
}

/**
 * A place URL that carries a body choice.
 *
 * `next.view` SETS the parameter; `null` REMOVES it (which is how the switcher
 * gets back to "whatever the merchant published"); `undefined` LEAVES it alone
 * (which is how a branch link keeps the guest's current view).
 *
 * The path is passed through untouched — this helper only owns the query. The
 * path itself is built by `placePath`, which is the one place a public guest
 * address is allowed to be assembled.
 */
export function buildPlaceMenuViewHref(
  pathname: string,
  search: string,
  next: {
    view?: PlaceMenuViewMode | null;
  },
): string {
  const params = new URLSearchParams(search);

  if (next.view !== undefined) {
    if (next.view) params.set(PLACE_MENU_VIEW_PARAM, next.view);
    else params.delete(PLACE_MENU_VIEW_PARAM);
  }

  const query = params.toString();
  return query ? `${pathname}?${query}` : pathname;
}
